import { useMutation } from '@tanstack/react-query';
import { updateBusiness } from '@Services/api/businessAddress.api';

/**
 * Type definition for the `onSuccess` callback function used in the `useMutation` hook.
 * 
 * This type is derived from the `useMutation` hook provided by `@tanstack/react-query`.
 * It specifies the type of the `onSuccess` callback function that is triggered when
 * the `updateBusiness` mutation is successful.
 * 
 * - The mutation function returns a promise that resolves to the data returned by `updateBusiness`.
 * - The error type is `Error`, which represents any error that might occur during the mutation.
 * - The variables type is the first parameter of `updateBusiness`, which is the input data for the mutation.
 * - The context type is `unknown`, which can be used to pass additional information to the mutation.
 * 
 * @param {ReturnType<typeof updateBusiness>} data - The data returned by the successful mutation.
 * @param {Parameters<typeof updateBusiness>[0]} variables - The input data used for the mutation.
 * @param {unknown} context - Additional context or information passed to the mutation.
 */
type UpdateBusinessAddressOnSuccess = Parameters<
  typeof useMutation<
    ReturnType<typeof updateBusiness>,
    Error,
    Parameters<typeof updateBusiness>[0],
    unknown
  >
>[0]['onSuccess'];

export const useUpdateBusinessAddress = (onSuccess: UpdateBusinessAddressOnSuccess) =>
  useMutation({
    mutationFn: updateBusiness,
    mutationKey: ['updateKnowYourBusiness'],
    onSuccess,
  });
